import { useRef, useState, useCallback } from 'react';

/**
 * Hook that detects multiple rapid clicks within a timeout window.
 * Returns [isActivated, handleClick].
 *
 * @param {number} count - Number of clicks required to activate (default: 3)
 * @param {number} timeout - Max time between clicks in ms (default: 500)
 * @returns {[boolean, Function]} [isActivated, handleClick]
 *
 * Usage:
 *   const [isActivated, handleClick] = useMultiClick();       // 3 clicks, 500ms
 *   const [isActivated, handleClick] = useMultiClick(5, 800); // 5 clicks, 800ms
 *   <span onClick={handleClick}>Click me</span>
 */
export default function useMultiClick(count = 3, timeout = 500) {
  const [activated, setActivated] = useState(false);
  const clickCountRef = useRef(0);
  const timerRef = useRef(null);

  const handleClick = useCallback(() => {
    clickCountRef.current++;
    clearTimeout(timerRef.current);
    if (clickCountRef.current >= count) {
      setActivated(true);
      clickCountRef.current = 0;
    } else {
      timerRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, timeout);
    }
  }, [count, timeout]);

  return [activated, handleClick];
}
